import { Button } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { MenuActionItem } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import React from 'react';
import { useIntl } from 'react-intl';

import { getAnnualReportCsv } from 'apis/merchant-payout-service/api';
import { getAnnualReportCsvLink } from 'apis/restaurant-api/merchant-admin/payouts/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';
import { usePermissions, hasPermissions } from 'common/wolt-permissions';

import { reportAnnualReportCsvDownload } from './behavioral';
import { AnnualReportDownloadStatus } from './types';

const annualReportOptions = [...Array(new Date().getFullYear() - 2020)].map((_, i) => 2020 + i); // We can get annual reports only 2020 and after

const OPS_TOOLS_DEV_URL = 'https://ops-tools.development.dev.woltapi.com';
const OPS_TOOLS_PROD_URL = 'https://ops.wolt.com';
const isProdEnv = process.env.APP_ENV === 'production';

type VenuePayoutReportActionsProps = {
  venue: Venue;
  setAnnualReportDownloadStatus: (status: AnnualReportDownloadStatus) => void;
};

export const VenuePayoutReportActions = ({ venue, setAnnualReportDownloadStatus }: VenuePayoutReportActionsProps) => {
  const { formatMessage } = useIntl();
  /*
   * Download annual report from Rapi. This is a fallback solution for venues that don't have proper json data. It's
   * very difficult to fix everything in MPS. So we have this solution to download the annual report from Rapi for
   * from 2020 & 2021.
   */
  const handleAnnualReportFromRapi = async (year: number) => {
    setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Downloading);

    try {
      const url = await getAnnualReportCsvLink(venue.id, year);
      downloadFromUrl(url.data);
      reportAnnualReportCsvDownload();
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial);
    } catch (e) {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Error);
    }
  };

  const handleAnnualReportCsv = async (year: number) => {
    try {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Downloading);
      const response = await getAnnualReportCsv(venue.id, year);
      downloadFileFromAxiosResponse(response);
      reportAnnualReportCsvDownload();
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial);
    } catch (_) {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Error);
    }
  };

  const permissions = usePermissions();

  const isPayoutsDeveloper = hasPermissions(
    [{ resource: 'payouts.maintenance', operations: ['c', 'r', 'u', 'd'] }],
    permissions,
  );
  return (
    <>
      <LegacyPopover
        placement="bottom-start"
        showOnHover={false}
        renderArrow={false}
        content={
          <div>
            {annualReportOptions.map(year => (
              <MenuActionItem
                variant="blue"
                key={year}
                onClick={() => (year <= 2021 ? handleAnnualReportFromRapi(year) : handleAnnualReportCsv(year))}
              >
                {year}
              </MenuActionItem>
            ))}
          </div>
        }
      >
        <Button size="small" variant="primary">
          {formatMessage({ id: 'payout-reports.download-annual-report' })}
        </Button>
      </LegacyPopover>
      {isPayoutsDeveloper && (
        <a
          href={
            isProdEnv
              ? `${OPS_TOOLS_PROD_URL}/venues/${venue.id}/payouts`
              : `${OPS_TOOLS_DEV_URL}/venues/${venue.id}/payouts`
          }
          target="_blank"
          rel="noreferrer"
          style={{ color: colors.wolt100, textDecoration: 'none' }}
        >
          <Button size="small" variant="primary">
            Open in opstools
          </Button>
        </a>
      )}
    </>
  );
};
